import React from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../LoadingSpinner';
import { AppSelect } from '../AppSelect';
import { DatePicker } from '../DatePicker';
import { ENTRY_TYPE_LABEL, ENTRY_TYPE_ORDER, type EmployeeTimeEntryType } from './employeeTimeEntryConstants';
import { PayrollPeriodClosedBanner } from './PayrollPeriodClosedHint';
import { TimeEntryIntervalModal } from '../timeEntries/TimeEntryIntervalModal';
import { isImportedTimeEntry, istEntryBadge } from '../../features/leads/components/detail/projectActivityIstHours';
import { useCapabilities } from '../../hooks/useCapabilities';
import { isHourTrackingEntryType, normalizeBreakMinutes, toHm } from '../../utils/employeeTimeEntryHours';
import {
  buildPayrollPeriodStatusMap,
  isClosedPayrollMonth,
  type PayrollPeriodListItem,
  type PayrollPeriodStatus,
} from '../../utils/payrollPeriodStatus';

type TimeEntry = {
  id: string;
  employee_id: string;
  entry_date: string;
  entry_type: EmployeeTimeEntryType;
  start_time: string | null;
  end_time: string | null;
  break_minutes?: number;
  note: string;
  activity_id?: string | null;
  /** Project time without appointment. */
  lead_id?: string | null;
  activity_title?: string | null;
  lead_title?: string | null;
  source?: string | null;
  edited_at?: string | null;
};

/** Appointment or project interval (not the day entry). */
function isLinkedInterval(e: TimeEntry): boolean {
  return e.entry_type === 'work' && Boolean(e.activity_id || e.lead_id);
}

function intervalTargetLabel(e: TimeEntry): string {
  if (e.activity_id) return `Termin: ${e.activity_title || 'ohne Titel'}`;
  return `Projekt: ${e.lead_title || 'ohne Titel'}`;
}

type EntryDraft = {
  entry_type: EmployeeTimeEntryType;
  start_time: string;
  end_time: string;
  break_minutes: string;
  note: string;
};

function parseLocaleNumber(value: unknown, fallback = 0): number {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const normalized = raw.replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function LabeledInput({
  label,
  type = 'text',
  value,
  onChange,
  disabled,
  min,
  step,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  min?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-app-text-muted">{label}</span>
      <input
        type={type}
        value={value}
        min={min}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-app-border-strong px-3 py-2 text-sm text-app-text outline-none focus:border-blue-500 disabled:bg-app-surface-muted disabled:text-app-text-subtle"
      />
    </label>
  );
}

export type EmployeeTimeEntryEditorProps = {
  employeeId: string;
  employeeName: string;
  entryDate: string;
  onSaved: () => void;
  onCancel: () => void;
  onError?: (message: string) => void;
  /** Appointment/project intervals changed (the editor stays open). */
  onIntervalsChanged?: () => void;
  /** Kompaktere Darstellung für Pop-over neben Tabellen. */
  compact?: boolean;
};

export function EmployeeTimeEntryEditor({
  employeeId,
  employeeName,
  entryDate,
  onSaved,
  onCancel,
  onError,
  onIntervalsChanged,
  compact = false,
}: EmployeeTimeEntryEditorProps) {
  const { can } = useCapabilities();
  const canEditIntervals = can('web.hr.edit');
  const [intervalDialog, setIntervalDialog] = React.useState<{ entry: TimeEntry | null } | null>(null);
  const [date, setDate] = React.useState(entryDate);
  const [draft, setDraft] = React.useState<EntryDraft>({
    entry_type: 'work',
    start_time: '',
    end_time: '',
    break_minutes: '0',
    note: '',
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [persistedId, setPersistedId] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  // Set when the day stamp would overlap appointment/project time (409 overlap).
  const [overlapWarning, setOverlapWarning] = React.useState<string | null>(null);
  const [activityRows, setActivityRows] = React.useState<TimeEntry[]>([]);
  const [payrollPeriodStatusMap, setPayrollPeriodStatusMap] = React.useState<
    Map<string, PayrollPeriodStatus>
  >(() => new Map());
  const [isPayrollApprover, setIsPayrollApprover] = React.useState(false);
  const [pendingChangeRequestDates, setPendingChangeRequestDates] = React.useState<Set<string>>(
    () => new Set(),
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [periodsRes, meRes, requestsRes] = await Promise.all([
          fetch('/api/employees/payroll-periods', { credentials: 'include' }),
          fetch('/api/me', { credentials: 'include' }),
          fetch('/api/employees/time-entry-change-requests?status=pending', { credentials: 'include' }),
        ]);
        if (cancelled) return;
        if (periodsRes.ok) {
          const json = (await periodsRes.json()) as { items?: PayrollPeriodListItem[] };
          setPayrollPeriodStatusMap(buildPayrollPeriodStatusMap(Array.isArray(json.items) ? json.items : []));
        }
        if (meRes.ok) {
          const me = (await meRes.json()) as { is_payroll_approver?: boolean };
          setIsPayrollApprover(Boolean(me.is_payroll_approver));
        }
        if (requestsRes.ok) {
          const reqJson = (await requestsRes.json()) as {
            items?: Array<{ employee_id: string; entry_date: string }>;
          };
          const dates = new Set(
            (Array.isArray(reqJson.items) ? reqJson.items : [])
              .filter((item) => item.employee_id === employeeId)
              .map((item) => item.entry_date),
          );
          setPendingChangeRequestDates(dates);
        }
      } catch {
        // Non-blocking: editor works without period metadata.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const loadEntry = React.useCallback(async (ymd: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const year = Number.parseInt(ymd.slice(0, 4), 10);
      const [entriesRes, holidaysRes] = await Promise.all([
        fetch(
          `/api/employees/${encodeURIComponent(employeeId)}/time-entries?from=${encodeURIComponent(ymd)}&to=${encodeURIComponent(ymd)}`,
        ),
        fetch(`/api/holidays?year=${year}`),
      ]);
      const entriesJson = (await entriesRes.json()) as { items?: TimeEntry[]; error?: string };
      const holidaysJson = (await holidaysRes.json()) as { items?: Array<{ holiday_date: string; name: string }> };
      if (!entriesRes.ok) throw new Error(entriesJson.error || 'Eintrag konnte nicht geladen werden.');

      const items = Array.isArray(entriesJson.items) ? entriesJson.items : [];
      setActivityRows(items.filter(isLinkedInterval));
      const existing = items.find((e) => !isLinkedInterval(e)) ?? null;
      if (existing) {
        setPersistedId(existing.id);
        setDraft({
          entry_type: existing.entry_type,
          start_time: toHm(existing.start_time),
          end_time: toHm(existing.end_time),
          break_minutes: String(normalizeBreakMinutes(existing.break_minutes)),
          note: existing.note || '',
        });
        return;
      }

      setPersistedId(null);
      const holiday = (Array.isArray(holidaysJson.items) ? holidaysJson.items : []).find((h) => h.holiday_date === ymd);
      if (holiday) {
        setDraft({
          entry_type: 'public_holiday',
          start_time: '',
          end_time: '',
          break_minutes: '0',
          note: holiday.name || '',
        });
      } else {
        setDraft({ entry_type: 'work', start_time: '', end_time: '', break_minutes: '0', note: '' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Eintrag konnte nicht geladen werden.';
      setLoadError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [employeeId, onError]);

  React.useEffect(() => {
    setDate(entryDate);
    void loadEntry(entryDate);
  }, [employeeId, entryDate, loadEntry]);

  const handleDateChange = (next: string) => {
    if (!next) return;
    setOverlapWarning(null);
    setDate(next);
    void loadEntry(next);
  };

  const save = async (confirmOverlap = false) => {
    setSaving(true);
    setNotice(null);
    setOverlapWarning(null);
    try {
      const res = await fetch(
        `/api/employees/${encodeURIComponent(employeeId)}/time-entries/${encodeURIComponent(date)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entry_type: draft.entry_type,
            start_time: draft.start_time || null,
            end_time: draft.end_time || null,
            break_minutes: isHourTrackingEntryType(draft.entry_type)
              ? normalizeBreakMinutes(parseLocaleNumber(draft.break_minutes, 0))
              : 0,
            note: draft.note,
            confirm_overlap: confirmOverlap,
          }),
        },
      );
      const data = (await res.json()) as {
        item?: TimeEntry;
        change_request?: { id: string };
        error?: string;
        code?: string;
      };
      if (res.status === 409 && data.code === 'overlap') {
        setOverlapWarning(data.error || 'Überschneidet sich mit Arbeitszeit auf einem Termin oder Projekt.');
        return;
      }
      if (res.status === 202 && data.change_request) {
        setPendingChangeRequestDates((prev) => new Set(prev).add(date));
        setNotice('Änderungsantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        onSaved();
        return;
      }
      if (!res.ok || !data.item) throw new Error(data.error || 'Zeiteintrag konnte nicht gespeichert werden.');
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gespeichert werden.';
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!persistedId) return;
    setDeleting(true);
    setNotice(null);
    try {
      const res = await fetch(
        `/api/employees/${encodeURIComponent(employeeId)}/time-entries/${encodeURIComponent(date)}`,
        { method: 'DELETE' },
      );
      const data = (await res.json()) as { ok?: boolean; change_request?: { id: string }; error?: string };
      if (res.status === 202 && data.change_request) {
        setPendingChangeRequestDates((prev) => new Set(prev).add(date));
        setNotice('Löschantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        onSaved();
        return;
      }
      if (!res.ok || !data.ok) throw new Error(data.error || 'Zeiteintrag konnte nicht gelöscht werden.');
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gelöscht werden.';
      onError?.(msg);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = Boolean(persistedId && !persistedId.startsWith('holiday-'));
  const showClosedBanner = isClosedPayrollMonth(date, payrollPeriodStatusMap);
  const showPendingBanner = pendingChangeRequestDates.has(date);

  return (
    <div
      className={`flex h-full min-h-0 flex-col bg-app-surface ${
        compact ? 'rounded-lg' : 'rounded-lg border border-app-border bg-app-surface-muted'
      }`}
    >
      <div
        className={`flex items-center justify-between gap-2 border-b border-app-border ${
          compact ? 'px-3 py-2' : 'px-4 py-3'
        }`}
      >
        <div className="min-w-0">
          <p className={`font-semibold text-app-text truncate ${compact ? 'text-xs' : 'text-sm'}`}>
            {employeeName}
          </p>
          <p className={`text-app-text-subtle ${compact ? 'text-xs' : 'text-xs'}`}>Zeiteintrag bearbeiten</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 rounded-md p-1 text-app-text-subtle hover:bg-app-surface hover:text-app-text-muted"
          aria-label="Bearbeitung schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className={`flex-1 min-h-0 overflow-y-auto ${compact ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
        {showClosedBanner ? (
          <PayrollPeriodClosedBanner isApprover={isPayrollApprover} className="mb-3" />
        ) : null}
        {showPendingBanner && !notice ? (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Für diesen Tag liegt eine beantragte Änderung vor.
          </div>
        ) : null}
        {notice ? (
          <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            {notice}
          </div>
        ) : null}
        {activityRows.length > 0 || canEditIntervals ? (
          <div className="mb-3 rounded-md border border-app-border bg-app-surface-muted px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-app-text-muted">Ist-Zeiten je Termin/Projekt</p>
              {canEditIntervals && !loading ? (
                <button
                  type="button"
                  onClick={() => setIntervalDialog({ entry: null })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-pipedrive-blue hover:underline"
                >
                  <Plus className="h-3 w-3" />
                  Intervall
                </button>
              ) : null}
            </div>
            {activityRows.length > 0 ? (
              <ul className="mt-1 space-y-1 text-xs text-app-text-muted">
                {activityRows.map((row) => {
                  const badge = istEntryBadge(row);
                  const label = (
                    <>
                      {(row.start_time ?? '').slice(0, 5) || '-'}–{(row.end_time ?? '').slice(0, 5) || 'offen'}
                      {` · ${intervalTargetLabel(row)}`}
                      {row.note ? ` · ${row.note}` : ''}
                      {badge ? <span className="ml-1 text-app-text-subtle">({badge})</span> : null}
                    </>
                  );
                  return (
                    <li key={row.id} title={isImportedTimeEntry(row) ? 'Aus MeinSHK übernommen – Änderungen bitte dort vornehmen.' : undefined}>
                      {canEditIntervals && !isImportedTimeEntry(row) ? (
                        <button
                          type="button"
                          onClick={() => setIntervalDialog({ entry: row })}
                          className="text-left hover:text-app-text hover:underline"
                        >
                          {label}
                        </button>
                      ) : (
                        label
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : null}
            <p className="mt-1 text-xs text-app-text-subtle">
              Der Tageseintrag unten (ohne Termin) ändert diese Intervalle nicht.
            </p>
          </div>
        ) : null}
        {loading ? (
          <InlineLoadingState label="Lade Eintrag…" className="justify-start" />
        ) : loadError ? (
          <p className="text-sm text-red-700">{loadError}</p>
        ) : (
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            <label className="block">
              <span className="text-xs font-medium text-app-text-muted">Datum</span>
              <DatePicker value={date} onChange={handleDateChange} className="mt-1 w-full" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-app-text-muted">Typ</span>
              <AppSelect
                value={draft.entry_type}
                onChange={(value) => {
                  const nextType = value as EmployeeTimeEntryType;
                  const tracksHours = isHourTrackingEntryType(nextType);
                  setDraft((prev) => ({
                    ...prev,
                    entry_type: nextType,
                    break_minutes: tracksHours ? prev.break_minutes : '0',
                    start_time: tracksHours ? prev.start_time : '',
                    end_time: tracksHours ? prev.end_time : '',
                  }));
                }}
                options={ENTRY_TYPE_ORDER.map((type) => ({
                  value: type,
                  label: ENTRY_TYPE_LABEL[type],
                }))}
                className="mt-1"
                size="md"
              />
            </label>
            <div className={`grid grid-cols-2 ${compact ? 'gap-2' : 'gap-3'}`}>
              <LabeledInput
                label="Start"
                type="time"
                value={draft.start_time}
                onChange={(v) => setDraft((prev) => ({ ...prev, start_time: v }))}
                disabled={!isHourTrackingEntryType(draft.entry_type)}
              />
              <LabeledInput
                label="Ende"
                type="time"
                value={draft.end_time}
                onChange={(v) => setDraft((prev) => ({ ...prev, end_time: v }))}
                disabled={!isHourTrackingEntryType(draft.entry_type)}
              />
            </div>
            {isHourTrackingEntryType(draft.entry_type) ? (
              <LabeledInput
                label="Pause (Minuten)"
                type="number"
                min={0}
                step={1}
                value={draft.break_minutes}
                onChange={(v) => setDraft((prev) => ({ ...prev, break_minutes: v }))}
              />
            ) : null}
            <label className="block">
              <span className="text-xs font-medium text-app-text-muted">Notiz</span>
              <textarea
                value={draft.note}
                onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
                className={`mt-1 w-full rounded-md border border-app-border-strong px-3 py-2 text-sm ${
                  compact ? 'min-h-14' : 'min-h-20'
                }`}
              />
            </label>
          </div>
        )}
      </div>

      {overlapWarning ? (
        <div className="mx-4 mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p>{overlapWarning}</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => void save(true)}
              disabled={saving}
              className="rounded-md border border-amber-300 bg-white px-2 py-1 font-medium hover:bg-amber-100 disabled:opacity-50"
            >
              Trotzdem speichern
            </button>
            <button type="button" onClick={() => setOverlapWarning(null)} className="px-2 py-1 hover:underline">
              Zeiten anpassen
            </button>
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 border-t border-app-border px-4 py-3">
        {canDelete ? (
          <button
            type="button"
            onClick={() => void remove()}
            disabled={deleting || saving || loading}
            className={`app-btn-danger-outline inline-flex items-center gap-1.5 rounded-md border border-red-200 font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 ${
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Löschen
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving || deleting}
            className={`rounded-md border border-app-border-strong disabled:opacity-50 ${
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            }`}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || deleting || loading}
            className={`inline-flex items-center gap-1.5 rounded-md bg-pipedrive-blue font-semibold text-white disabled:opacity-50 ${
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            }`}
          >
            {saving ? <LoadingSpinner size="sm" /> : null}
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
      <TimeEntryIntervalModal
        open={intervalDialog !== null}
        onClose={() => setIntervalDialog(null)}
        onSaved={(message) => {
          setIntervalDialog(null);
          setNotice(message ?? null);
          void loadEntry(date);
          onIntervalsChanged?.();
        }}
        scope={{ kind: 'employee', employeeId, employeeName }}
        entry={
          intervalDialog?.entry
            ? {
                id: intervalDialog.entry.id,
                employee_id: employeeId,
                activity_id: intervalDialog.entry.activity_id ?? null,
                lead_id: intervalDialog.entry.lead_id ?? null,
                entry_date: intervalDialog.entry.entry_date,
                start_time: intervalDialog.entry.start_time,
                end_time: intervalDialog.entry.end_time,
                break_minutes: intervalDialog.entry.break_minutes,
                note: intervalDialog.entry.note,
              }
            : null
        }
        defaults={{ target: 'activity', date }}
      />
    </div>
  );
}
